import { FC, useCallback, useEffect, useState } from 'react'
import {
	Button,
	Drawer,
	Form,
	Input,
	List,
	message,
	Modal,
	Popconfirm,
	Segmented,
	Select,
	Space,
	Spin,
	Switch,
	Table,
	Tag,
	Upload
} from 'antd'
import {
	ArrowLeftOutlined,
	CloudUploadOutlined,
	DeleteOutlined,
	EditOutlined,
	FileOutlined,
	FilePdfOutlined,
	PictureOutlined,
	PlayCircleOutlined,
	PlusOutlined,
	ReadOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { learningStore } from '../store/learning'
import {
	CreateMaterialPayload,
	CreateResourcePayload,
	LearningCategory,
	LearningCategoryLabels,
	LearningMaterial,
	LearningResource,
	ResourceType,
	UpdateMaterialPayload
} from '../helpers/types/learning'
import { RNLearning } from '../helpers/routeNames'
import { PageBreadcrumb } from '../components/PageBreadcrumb'
import { RichTextEditor } from '../components/RichTextEditor'
import { AxiosError } from 'axios'
import type {
	RcFile,
	UploadRequestOption as RcCustomRequestOptions
} from 'rc-upload/lib/interface'
import { deleteFileApi, fetchMaterialById, uploadLearningFileApi } from '../api/learning'

const categoryOptions = Object.values(LearningCategory).map((c) => ({
	label: LearningCategoryLabels[c],
	value: c
}))

export const LearningAdmin: FC = () => {
	const navigate = useNavigate()
	const {
		materials,
		total,
		loading,
		fetchMaterialsAdmin,
		deleteMaterial,
		publishMaterial,
		unpublishMaterial
	} = learningStore()

	const [page, setPage] = useState(1)
	const [createOpen, setCreateOpen] = useState(false)
	const [editMaterial, setEditMaterial] =
		useState<LearningMaterial | null>(null)
	const [resourceMaterial, setResourceMaterial] =
		useState<LearningMaterial | null>(null)

	useEffect(() => {
		fetchMaterialsAdmin({ page, limit: 20 })
	}, [page, fetchMaterialsAdmin])

	const handleDelete = async (id: string) => {
		try {
			await deleteMaterial(id)
			message.success('Материал удалён')
		} catch (err) {
			if (err instanceof AxiosError) {
				message.error(
					err.response?.data?.message ||
						'Ошибка при удалении'
				)
			}
		}
	}

	const handleTogglePublish = async (
		material: LearningMaterial
	) => {
		try {
			if (material.isPublished) {
				await unpublishMaterial(material.id)
				message.success('Материал снят с публикации')
			} else {
				await publishMaterial(material.id)
				message.success('Материал опубликован')
			}
		} catch (err) {
			if (err instanceof AxiosError) {
				message.error(
					err.response?.data?.message ||
						'Ошибка при изменении статуса'
				)
			}
		}
	}

	const columns = [
		{
			title: 'Название',
			dataIndex: 'title',
			key: 'title',
			ellipsis: true
		},
		{
			title: 'Категория',
			dataIndex: 'category',
			key: 'category',
			width: 180,
			render: (cat: string) => <Tag color="blue">{cat}</Tag>
		},
		{
			title: 'Опубликован',
			dataIndex: 'isPublished',
			key: 'isPublished',
			width: 140,
			render: (_: boolean, record: LearningMaterial) => (
				<Switch
					checked={record.isPublished}
					onChange={() => handleTogglePublish(record)}
					checkedChildren="Да"
					unCheckedChildren="Нет"
				/>
			)
		},
		{
			title: 'Ресурсы',
			key: 'resources',
			width: 100,
			render: (_: unknown, record: LearningMaterial) => (
				<Tag>{
					(record.pdfCount || 0) +
					(record.videoCount || 0)
				}</Tag>
			)
		},
		{
			title: 'Действия',
			key: 'actions',
			width: 200,
			render: (_: unknown, record: LearningMaterial) => (
				<Space size="small">
					<Button
						size="small"
						icon={<EditOutlined />}
						onClick={() => setEditMaterial(record)}
					/>
					<Button
						size="small"
						icon={<PlusOutlined />}
						onClick={() => setResourceMaterial(record)}>
						Ресурс
					</Button>
					<Popconfirm
						title="Удалить материал?"
						onConfirm={() => handleDelete(record.id)}>
						<Button
							size="small"
							danger
							icon={<DeleteOutlined />}
						/>
					</Popconfirm>
				</Space>
			)
		}
	]

	return (
		<div style={{ padding: '16px', paddingBottom: '90px' }}>
			<PageBreadcrumb
				items={[
					{ label: 'База знаний', path: RNLearning },
					{ label: 'Управление' }
				]}
			/>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: 16
				}}>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 12
					}}>
					<Button
						icon={<ArrowLeftOutlined />}
						onClick={() => navigate(RNLearning)}>
						Назад
					</Button>
					<h1 style={{ margin: 0 }}>
						Управление материалами
					</h1>
				</div>
				<Button
					type="primary"
					icon={<PlusOutlined />}
					onClick={() => setCreateOpen(true)}>
					Создать
				</Button>
			</div>

			<Table
				dataSource={materials.map((m) => ({
					...m,
					key: m.id
				}))}
				columns={columns}
				loading={loading}
				pagination={{
					current: page,
					pageSize: 20,
					total: total,
					onChange: setPage,
					showSizeChanger: false
				}}
				scroll={{ x: 700 }}
			/>

			<CreateMaterialModal
				open={createOpen}
				onClose={() => {
					setCreateOpen(false)
					fetchMaterialsAdmin({ page, limit: 20 })
				}}
			/>

			<EditMaterialModal
				material={editMaterial}
				onClose={() => {
					setEditMaterial(null)
					fetchMaterialsAdmin({ page, limit: 20 })
				}}
			/>

			<ResourceEditorDrawer
				material={resourceMaterial}
				onClose={() => {
					setResourceMaterial(null)
					fetchMaterialsAdmin({ page, limit: 20 })
				}}
			/>
		</div>
	)
}

// ── Create Material Modal ───────────────────────────────────

const CreateMaterialModal: FC<{
	open: boolean
	onClose: () => void
}> = ({ open, onClose }) => {
	const [form] = Form.useForm()
	const { createMaterial } = learningStore()
	const [submitting, setSubmitting] = useState(false)

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields()
			setSubmitting(true)
			const payload: CreateMaterialPayload = {
				material: {
					title: values.title,
					description: values.description,
					category: values.category,
					subcategory: values.subcategory,
					isPublished: values.isPublished ?? false
				},
				resources: []
			}
			await createMaterial(payload)
			message.success('Материал создан')
			form.resetFields()
			onClose()
		} catch (err) {
			if (err instanceof AxiosError) {
				message.error(
					err.response?.data?.message ||
						'Ошибка при создании'
				)
			}
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<Modal
			title="Создать материал"
			open={open}
			onOk={handleSubmit}
			onCancel={() => {
				form.resetFields()
				onClose()
			}}
			confirmLoading={submitting}
			okText="Создать"
			cancelText="Отмена">
			<Form form={form} layout="vertical">
				<Form.Item
					name="title"
					label="Название"
					rules={[
						{
							required: true,
							message: 'Введите название'
						}
					]}>
					<Input />
				</Form.Item>
				<Form.Item name="description" label="Описание">
					<Input.TextArea rows={3} />
				</Form.Item>
				<Form.Item
					name="category"
					label="Категория"
					rules={[
						{
							required: true,
							message: 'Выберите категорию'
						}
					]}>
					<Select options={categoryOptions} />
				</Form.Item>
				<Form.Item name="subcategory" label="Подкатегория">
					<Input />
				</Form.Item>
				<Form.Item
					name="isPublished"
					label="Опубликовать сейчас"
					valuePropName="checked">
					<Switch />
				</Form.Item>
			</Form>
		</Modal>
	)
}

// ── Edit Material Modal ─────────────────────────────────────

const EditMaterialModal: FC<{
	material: LearningMaterial | null
	onClose: () => void
}> = ({ material, onClose }) => {
	const [form] = Form.useForm()
	const { updateMaterial } = learningStore()
	const [submitting, setSubmitting] = useState(false)

	useEffect(() => {
		if (material) {
			form.setFieldsValue({
				title: material.title,
				description: material.description,
				category: material.category,
				subcategory: material.subcategory
			})
		}
	}, [material, form])

	const handleSubmit = async () => {
		if (!material) return
		try {
			const values = await form.validateFields()
			setSubmitting(true)
			const updates: UpdateMaterialPayload = {
				title: values.title,
				description: values.description,
				category: values.category,
				subcategory: values.subcategory
			}
			await updateMaterial(material.id, updates)
			message.success('Материал обновлён')
			form.resetFields()
			onClose()
		} catch (err) {
			if (err instanceof AxiosError) {
				message.error(
					err.response?.data?.message ||
						'Ошибка при обновлении'
				)
			}
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<Modal
			title="Редактировать материал"
			open={!!material}
			onOk={handleSubmit}
			onCancel={() => {
				form.resetFields()
				onClose()
			}}
			confirmLoading={submitting}
			okText="Сохранить"
			cancelText="Отмена">
			<Form form={form} layout="vertical">
				<Form.Item
					name="title"
					label="Название"
					rules={[
						{
							required: true,
							message: 'Введите название'
						}
					]}>
					<Input />
				</Form.Item>
				<Form.Item name="description" label="Описание">
					<Input.TextArea rows={3} />
				</Form.Item>
				<Form.Item
					name="category"
					label="Категория"
					rules={[
						{
							required: true,
							message: 'Выберите категорию'
						}
					]}>
					<Select options={categoryOptions} />
				</Form.Item>
				<Form.Item name="subcategory" label="Подкатегория">
					<Input />
				</Form.Item>
			</Form>
		</Modal>
	)
}

// ── Resource Editor Drawer ──────────────────────────────────

function getResourceIcon(type: ResourceType) {
	switch (type) {
		case ResourceType.PDF:
			return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
		case ResourceType.VIDEO:
			return <PlayCircleOutlined style={{ color: '#722ed1', fontSize: 16 }} />
		case ResourceType.IMAGE:
			return <PictureOutlined style={{ color: '#1677ff', fontSize: 16 }} />
		case ResourceType.FILE:
			return <FileOutlined style={{ color: '#faad14', fontSize: 16 }} />
		case ResourceType.TEXT:
			return <ReadOutlined style={{ color: '#52c41a', fontSize: 16 }} />
		default:
			return <FileOutlined style={{ fontSize: 16 }} />
	}
}

const ResourceEditorDrawer: FC<{
	material: LearningMaterial | null
	onClose: () => void
}> = ({ material, onClose }) => {
	const [form] = Form.useForm()
	const { addResource, deleteResource } = learningStore()
	const [resources, setResources] = useState<LearningResource[]>([])
	const [loadingRes, setLoadingRes] = useState(false)
	const [resourceType, setResourceType] = useState<ResourceType>(
		ResourceType.PDF
	)
	const [fileUploading, setFileUploading] = useState(false)
	const [submitting, setSubmitting] = useState(false)
	const [richContent, setRichContent] = useState('')

	const fileUrl = Form.useWatch('fileUrl', form)
	const hasFile = !!fileUrl

	const loadResources = useCallback(async () => {
		if (!material) return
		setLoadingRes(true)
		try {
			const mat = await fetchMaterialById(material.id)
			setResources(mat.resources || [])
		} finally {
			setLoadingRes(false)
		}
	}, [material])

	useEffect(() => {
		if (material) {
			loadResources()
			form.resetFields()
			setRichContent('')
			setResourceType(ResourceType.PDF)
		} else {
			setResources([])
		}
	}, [material, loadResources, form])

	const handleDeleteResource = async (resourceId: string) => {
		if (!material) return
		try {
			await deleteResource(material.id, resourceId)
			setResources((prev) => prev.filter((r) => r.id !== resourceId))
			message.success('Ресурс удалён')
		} catch (err) {
			if (err instanceof AxiosError) {
				message.error(
					err.response?.data?.message || 'Ошибка при удалении'
				)
			}
		}
	}

	const handleDeleteFile = async () => {
		const url = form.getFieldValue('fileUrl')
		form.setFieldsValue({
			fileUrl: undefined,
			fileName: undefined,
			fileSize: undefined,
			mimeType: undefined
		})
		if (url) {
			try {
				await deleteFileApi(url)
			} catch (err) {
				if (err instanceof AxiosError) {
					message.error(
						err.response?.data?.message || 'Ошибка при удалении файла из S3'
					)
				}
			}
		}
	}

	const uploadFile = async (options: RcCustomRequestOptions) => {
		const file = options.file as RcFile

		if (resourceType === ResourceType.IMAGE && !file.type.startsWith('image/')) {
			message.error('Принимаются только изображения')
			options.onError?.(new Error('Invalid file type'))
			return
		}
		if (resourceType === ResourceType.PDF && file.type !== 'application/pdf') {
			message.error('Можно загружать только PDF')
			options.onError?.(new Error('Invalid file type'))
			return
		}
		const maxMb = resourceType === ResourceType.IMAGE ? 15 : 100
		if (file.size > maxMb * 1024 * 1024) {
			message.error(`Максимальный размер: ${maxMb} МБ`)
			options.onError?.(new Error('File too large'))
			return
		}

		try {
			setFileUploading(true)
			const response = await uploadLearningFileApi(file)
			form.setFieldsValue({
				fileUrl: response.fileUrl,
				fileName: response.fileName,
				fileSize: response.fileSize,
				mimeType: response.mimeType
			})
			if (!form.getFieldValue('title')) {
				form.setFieldValue('title', response.fileName)
			}
			message.success('Файл загружен')
			options.onSuccess?.(response)
		} catch (err) {
			if (err instanceof AxiosError) {
				message.error(
					err.response?.data?.message || 'Ошибка при загрузке'
				)
			}
			options.onError?.(new Error('Upload failed'))
		} finally {
			setFileUploading(false)
		}
	}

	const handleAdd = async () => {
		if (!material) return
		try {
			const values = await form.validateFields()
			setSubmitting(true)
			const resource: CreateResourcePayload = {
				type: resourceType,
				title: values.title,
				description: values.description
			}
			switch (resourceType) {
				case ResourceType.TEXT:
					resource.content = richContent
					break
				case ResourceType.PDF:
				case ResourceType.IMAGE:
				case ResourceType.FILE:
					resource.fileUrl = values.fileUrl
					resource.fileName = values.fileName
					resource.fileSize = values.fileSize
					resource.mimeType = values.mimeType
					break
				case ResourceType.VIDEO:
					resource.videoUrl = values.videoUrl
					break
			}
			await addResource(material.id, resource)
			message.success('Ресурс добавлен')
			form.resetFields()
			setRichContent('')
			await loadResources()
		} catch (err) {
			if (err instanceof AxiosError) {
				message.error(
					err.response?.data?.message ||
						'Ошибка при добавлении ресурса'
				)
			}
		} finally {
			setSubmitting(false)
		}
	}

	const typeOptions = [
		{ label: 'Страница', value: ResourceType.TEXT },
		{ label: 'Фото', value: ResourceType.IMAGE },
		{ label: 'PDF', value: ResourceType.PDF },
		{ label: 'Файл', value: ResourceType.FILE },
		{ label: 'Видео', value: ResourceType.VIDEO }
	]

	return (
		<Drawer
			title={
				<span>
					Ресурсы —{' '}
					<span style={{ fontWeight: 400 }}>
						{material?.title}
					</span>
				</span>
			}
			open={!!material}
			onClose={() => {
				form.resetFields()
				setRichContent('')
				onClose()
			}}
			width={Math.min(860, window.innerWidth - 32)}
			zIndex={1200}
			styles={{
				body: { padding: 0, overflowY: 'auto' },
				footer: { padding: '12px 24px' }
			}}
			destroyOnClose
			footer={
				<Button
					type="primary"
					block
					size="large"
					loading={submitting || fileUploading}
					onClick={handleAdd}>
					Добавить ресурс
				</Button>
			}>
			{/* ── Existing resources ── */}
			<div
				style={{
					padding: '20px 24px',
					borderBottom: '1px solid #f0f0f0'
				}}>
				<h3 style={{ margin: '0 0 14px', fontSize: 15 }}>
					Существующие ресурсы
				</h3>
				{loadingRes ? (
					<div style={{ textAlign: 'center', padding: 24 }}>
						<Spin />
					</div>
				) : resources.length === 0 ? (
					<p style={{ color: '#bbb', margin: 0 }}>
						Пока нет ресурсов
					</p>
				) : (
					<List
						size="small"
						dataSource={[...resources].sort(
							(a, b) => a.order - b.order
						)}
						renderItem={(r) => (
							<List.Item
								style={{
									padding: '8px 0',
									borderBottom: '1px solid #f5f5f5'
								}}
								actions={[
									<Popconfirm
										key="del"
										title="Удалить этот ресурс?"
										onConfirm={() =>
											handleDeleteResource(r.id)
										}>
										<Button
											size="small"
											danger
											icon={<DeleteOutlined />}
										/>
									</Popconfirm>
								]}>
								<List.Item.Meta
									avatar={getResourceIcon(r.type)}
									title={
										<span style={{ fontSize: 14 }}>
											{r.title}
										</span>
									}
									description={
										r.description ||
										r.fileName ||
										undefined
									}
								/>
							</List.Item>
						)}
					/>
				)}
			</div>

			{/* ── Add new resource ── */}
			<div style={{ padding: '20px 24px' }}>
				<h3 style={{ margin: '0 0 16px', fontSize: 15 }}>
					Добавить ресурс
				</h3>

				{/* Type selector */}
				<div
					style={{
						marginBottom: 20,
						padding: '14px 16px',
						background: '#fafafa',
						borderRadius: 8,
						border: '1px solid #f0f0f0'
					}}>
					<div
						style={{
							fontSize: 12,
							color: '#888',
							marginBottom: 8,
							textTransform: 'uppercase',
							letterSpacing: '0.05em'
						}}>
						Тип ресурса
					</div>
					<Segmented
						options={typeOptions}
						value={resourceType}
						onChange={(v) => {
							setResourceType(v as ResourceType)
							form.resetFields([
								'fileUrl',
								'fileName',
								'fileSize',
								'mimeType',
								'videoUrl'
							])
						}}
						block
					/>
				</div>

				<Form form={form} layout="vertical">
					<Form.Item
						name="title"
						label="Название"
						rules={[
							{
								required: true,
								message: 'Введите название'
							}
						]}>
						<Input placeholder="Название ресурса" />
					</Form.Item>
					<Form.Item
						name="description"
						label="Краткое описание">
						<Input.TextArea
							rows={2}
							placeholder="Необязательно"
						/>
					</Form.Item>

					{/* ── TEXT / PAGE ── */}
					{resourceType === ResourceType.TEXT && (
						<Form.Item label="Содержимое страницы">
							<RichTextEditor
								value={richContent}
								onChange={setRichContent}
								placeholder="Напишите содержимое страницы…"
								minHeight={300}
							/>
						</Form.Item>
					)}

					{/* ── IMAGE ── */}
					{resourceType === ResourceType.IMAGE && (
						<Form.Item label="Изображение">
							{hasFile ? (
								<div>
									<img
										src={form.getFieldValue('fileUrl')}
										alt="preview"
										style={{
											maxWidth: '100%',
											maxHeight: 200,
											borderRadius: 8,
											display: 'block'
										}}
									/>
									<Button
										size="small"
										danger
										style={{ marginTop: 8 }}
										onClick={handleDeleteFile}>
										Удалить
									</Button>
								</div>
							) : (
								<Upload.Dragger
									accept="image/*"
									maxCount={1}
									customRequest={uploadFile}
									showUploadList={false}
									disabled={fileUploading}
									style={{ padding: '24px 0' }}>
									<PictureOutlined
										style={{
											fontSize: 36,
											color: '#1677ff',
											display: 'block',
											marginBottom: 8
										}}
									/>
									<p style={{ margin: 0, fontWeight: 500 }}>
										Перетащите изображение или нажмите
									</p>
									<p
										style={{
											margin: '4px 0 0',
											color: '#999',
											fontSize: 12
										}}>
										JPG, PNG, GIF, WebP — до 15 МБ
									</p>
								</Upload.Dragger>
							)}
						</Form.Item>
					)}

					{/* ── PDF ── */}
					{resourceType === ResourceType.PDF && (
						<Form.Item label="PDF-файл">
							{hasFile ? (
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: 12,
										padding: '10px 16px',
										background: '#fff2f0',
										border: '1px solid #ffccc7',
										borderRadius: 8
									}}>
									<FilePdfOutlined
										style={{
											fontSize: 28,
											color: '#ff4d4f'
										}}
									/>
									<span style={{ flex: 1, fontSize: 13 }}>
										{form.getFieldValue('fileName')}
									</span>
									<Button
										size="small"
										danger
										onClick={handleDeleteFile}>
										Удалить
									</Button>
								</div>
							) : (
								<Upload.Dragger
									accept=".pdf,application/pdf"
									maxCount={1}
									customRequest={uploadFile}
									showUploadList={false}
									disabled={fileUploading}
									style={{ padding: '24px 0' }}>
									<FilePdfOutlined
										style={{
											fontSize: 36,
											color: '#ff4d4f',
											display: 'block',
											marginBottom: 8
										}}
									/>
									<p style={{ margin: 0, fontWeight: 500 }}>
										Перетащите PDF или нажмите для выбора
									</p>
									<p
										style={{
											margin: '4px 0 0',
											color: '#999',
											fontSize: 12
										}}>
										Только PDF — до 100 МБ
									</p>
								</Upload.Dragger>
							)}
						</Form.Item>
					)}

					{/* ── ANY FILE ── */}
					{resourceType === ResourceType.FILE && (
						<Form.Item label="Файл">
							{hasFile ? (
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: 12,
										padding: '10px 16px',
										background: '#fffbe6',
										border: '1px solid #ffe58f',
										borderRadius: 8
									}}>
									<FileOutlined
										style={{
											fontSize: 28,
											color: '#faad14'
										}}
									/>
									<span style={{ flex: 1, fontSize: 13 }}>
										{form.getFieldValue('fileName')}
									</span>
									<Button
										size="small"
										danger
										onClick={handleDeleteFile}>
										Удалить
									</Button>
								</div>
							) : (
								<Upload.Dragger
									maxCount={1}
									customRequest={uploadFile}
									showUploadList={false}
									disabled={fileUploading}
									style={{ padding: '24px 0' }}>
									<CloudUploadOutlined
										style={{
											fontSize: 36,
											color: '#faad14',
											display: 'block',
											marginBottom: 8
										}}
									/>
									<p style={{ margin: 0, fontWeight: 500 }}>
										Перетащите файл или нажмите для выбора
									</p>
									<p
										style={{
											margin: '4px 0 0',
											color: '#999',
											fontSize: 12
										}}>
										Word, Excel, ZIP и любые другие — до 100 МБ
									</p>
								</Upload.Dragger>
							)}
						</Form.Item>
					)}

					{/* ── VIDEO ── */}
					{resourceType === ResourceType.VIDEO && (
						<Form.Item
							name="videoUrl"
							label="YouTube URL"
							rules={[
								{
									required: true,
									message: 'Введите ссылку на видео'
								}
							]}>
							<Input
								prefix={
									<PlayCircleOutlined
										style={{ color: '#722ed1' }}
									/>
								}
								placeholder="https://www.youtube.com/watch?v=..."
							/>
						</Form.Item>
					)}

					{/* Hidden file fields */}
					<Form.Item name="fileUrl" hidden>
						<Input />
					</Form.Item>
					<Form.Item name="fileName" hidden>
						<Input />
					</Form.Item>
					<Form.Item name="fileSize" hidden>
						<Input />
					</Form.Item>
					<Form.Item name="mimeType" hidden>
						<Input />
					</Form.Item>

					<Button
						type="primary"
						block
						size="large"
						loading={submitting || fileUploading}
						onClick={handleAdd}
						style={{ display: 'none' }}>
						Добавить ресурс
					</Button>
				</Form>
			</div>
		</Drawer>
	)
}
